/**
 * Mention extension factory for the 3mote MarkdownEditor.
 *
 * Wires `@tiptap/extension-mention` into our markdown round-trip pipeline
 * per ADR-003. Mentions are stored on the wire as ordinary GFM markdown
 * links with the custom `mention://user/{uuid}` protocol:
 *
 *   [@David](mention://user/123e4567-...-426614174000)
 *
 * Inside the editor (and in read-only rendering of comments / card
 * descriptions) they appear as styled chips. AI agents reading the same
 * markdown column see plain markdown links — no ProseMirror / HTML
 * required.
 *
 * Round-trip strategy:
 *
 *   1. **Serialize.** Override the mention node's storage.markdown.serialize
 *      to emit `[@{label}](mention://user/{id})`. tiptap-markdown's
 *      MarkdownSerializer reads `extension.storage.markdown.serialize`
 *      via `getMarkdownSpec()` (see node_modules/tiptap-markdown/src/util/extensions.js).
 *
 *   2. **Parse.** tiptap-markdown's parser pipeline runs markdown-it,
 *      then extensions can hook `parse.updateDOM(element)` to mutate
 *      the rendered HTML before TipTap parses it. We walk the DOM for
 *      `a[href^="mention://user/"]` and replace each anchor with a
 *      `<span data-type="mention" data-id="{uuid}" data-label="{name}">`,
 *      which is exactly the shape the Mention node's `parseHTML` matches.
 *
 * Why not register a markdown-it plugin via `parse.setup`?
 *   The DOM-level rewrite in `updateDOM` is simpler and uses the existing
 *   markdown-it link tokenization unchanged — no fragile token surgery.
 *   It's also the same pattern tiptap-markdown's task-list extension uses
 *   (see `src/extensions/nodes/task-item.js`), so there's project
 *   precedent.
 *
 * The round-trip property test in
 * `src/editor/__tests__/markdown-roundtrip.test.ts` locks in
 * `[@Name](mention://user/UUID)` as a stable fixed-point.
 */

import Mention, { type MentionOptions } from '@tiptap/extension-mention'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

const MENTION_URL_PREFIX = 'mention://user/'
/**
 * Channel-wide @all special mention. Reserved id + URL — never collides
 * with a real user UUID (UUIDs are 36 chars). On the wire:
 *   [@all](mention://all)
 * The BE expands this to all conversation participants minus the author
 * at `extractAndPersistChatMentions`; the FE picker prepends the entry
 * for channels only (see `useChatUserMentionSource`).
 */
const ALL_MENTION_URL = 'mention://all'
const ALL_MENTION_ID = 'all'

/**
 * Markdown serializer for the mention node. Emits the canonical
 * `[@{label}](mention://user/{id})` form — or `[@all](mention://all)`
 * for the channel-wide special mention.
 *
 * tiptap-markdown's serializer state has a `renderInline` and a `text`
 * method, but the cleanest path for a leaf inline node like mention is
 * to write the raw markdown directly via `state.write`.
 */
function serializeMention(state: { write: (s: string) => void }, node: ProseMirrorNode) {
  const id = (node.attrs.id ?? '') as string
  if (id === ALL_MENTION_ID) {
    state.write(`[@all](${ALL_MENTION_URL})`)
    return
  }
  const rawLabel = (node.attrs.label ?? node.attrs.id ?? '') as string
  // Escape characters that would break a markdown link's text or URL.
  // Names with `]` or `)` are vanishingly rare in practice but we
  // defensively backslash-escape them so the serialized form is always
  // syntactically valid GFM.
  const safeLabel = rawLabel.replace(/]/g, '\\]')
  const safeId = id.replace(/\)/g, '%29')
  state.write(`[@${safeLabel}](${MENTION_URL_PREFIX}${safeId})`)
}

/**
 * Walk the markdown-it-rendered DOM and convert
 * `<a href="mention://user/UUID">@Name</a>` anchors into
 * `<span data-type="mention" data-id="UUID" data-label="Name">@Name</span>`.
 *
 * Runs once per parse, before TipTap converts the HTML into a ProseMirror
 * document. The Mention node's `parseHTML` matches `span[data-type="mention"]`,
 * so the resulting span deserializes into a real mention node.
 */
function rewriteMentionAnchors(element: Element) {
  // Direct @user mentions: `mention://user/{uuid}`
  const userAnchors = element.querySelectorAll(`a[href^="${MENTION_URL_PREFIX}"]`)
  for (const anchor of userAnchors) {
    const href = anchor.getAttribute('href') ?? ''
    const id = href.slice(MENTION_URL_PREFIX.length)
    // Strip the leading `@` from the visible label if present (the
    // serializer always emits `@Name`, so the anchor text is `@Name`;
    // we want the label attribute to be just `Name`).
    const text = (anchor.textContent ?? '').replace(/^@/, '')

    const span = anchor.ownerDocument.createElement('span')
    span.setAttribute('data-type', 'mention')
    span.setAttribute('data-id', id)
    if (text) span.setAttribute('data-label', text)
    span.setAttribute('data-mention-suggestion-char', '@')
    span.textContent = `@${text}`
    anchor.replaceWith(span)
  }
  // Channel-wide @all: `mention://all` (no UUID path segment).
  const allAnchors = element.querySelectorAll(`a[href="${ALL_MENTION_URL}"]`)
  for (const anchor of allAnchors) {
    const span = anchor.ownerDocument.createElement('span')
    span.setAttribute('data-type', 'mention')
    span.setAttribute('data-id', ALL_MENTION_ID)
    span.setAttribute('data-label', 'all')
    span.setAttribute('data-mention-suggestion-char', '@')
    span.textContent = '@all'
    anchor.replaceWith(span)
  }
}

export interface BuildMentionExtensionOptions {
  /**
   * Optional suggestion config. Phase 3b live editor passes the
   * autocomplete-popover wiring through; the round-trip spike test
   * leaves it undefined (no UI).
   */
  suggestion?: MentionOptions['suggestion']
}

/**
 * Build the configured Mention extension. Returns a fresh instance per
 * call so each editor gets its own.
 */
export function buildMentionExtension({ suggestion }: BuildMentionExtensionOptions = {}) {
  return Mention.extend({
    /**
     * Hook into tiptap-markdown's per-extension storage. The serializer
     * emits the canonical `[@Name](mention://user/UUID)` form; the
     * parser walks the rendered DOM and rewrites mention anchors into
     * mention spans before TipTap consumes the HTML.
     */
    addStorage() {
      // `addStorage` is called by TipTap during extension instantiation
      // and merged with anything `Mention` itself defines. We add the
      // `markdown` slot tiptap-markdown's `getMarkdownSpec` looks for.
      return {
        markdown: {
          serialize: serializeMention,
          parse: {
            updateDOM(element: Element) {
              rewriteMentionAnchors(element)
            },
          },
        },
      }
    },
  }).configure({
    HTMLAttributes: {
      class: 'mention-chip',
    },
    // Render the chip text as `@{name}` — both the editor view and the
    // read-only renderer use this.
    renderText({ node }) {
      const label = (node.attrs.label ?? node.attrs.id ?? '') as string
      return `@${label}`
    },
    // Render the chip HTML with `data-user-id` for the user UUID.
    // Tailwind styling lives on `.mention-chip` in src/styles/global.css.
    //
    // The Mention extension calls our renderHTML with `options` that has
    // a pre-merged `HTMLAttributes` (data-type="mention" + class="mention-chip"
    // + any node-attribute renderHTML output like `data-id`). Spread it
    // and add `data-user-id`. When we return a DOMOutputSpec array
    // (rather than a string), the wrapper uses our return verbatim — so
    // we MUST include the merged attributes ourselves.
    renderHTML({ options, node }) {
      const id = (node.attrs.id ?? '') as string
      const isAll = id === ALL_MENTION_ID
      const label = isAll ? 'all' : ((node.attrs.label ?? node.attrs.id ?? '') as string)
      const attrs: Record<string, string> = {
        ...options.HTMLAttributes,
        'data-user-id': id,
      }
      // Tag @all chips for the CSS variant + the FE bell-line renderer.
      if (isAll) attrs['data-mention-kind'] = 'all'
      // The label lives in an INNER span so the outer chip can keep
      // `overflow: visible` and therefore a real text baseline; the inner
      // one owns the 18ch ellipsis. See `.mention-chip` in global.css for
      // why the truncation cannot live on the outer box.
      return ['span', attrs, ['span', { class: 'mention-chip__label' }, `@${label}`]]
    },
    deleteTriggerWithBackspace: true,
    suggestion: suggestion ?? { char: '@' },
  })
}
